import React, {PropsWithChildren, useCallback, useContext, useEffect} from 'react';
import enUS from 'antd/locale/en_US';
import elGR from 'antd/locale/el_GR';
import {App, ConfigProvider, ConfigProviderProps} from "antd";
import dayjs from "dayjs";
import i18n from "i18next";
import {initReactI18next} from "react-i18next";
import englishTranslations from "./translations-en.json";
import greekTranslations from "./translations-el.json";
import {GoogleReCaptchaProvider} from "react-google-recaptcha-v3";

export type LocaleType = ConfigProviderProps["locale"];
export type LocaleChoice = {
    key: string;
    value: LocaleType;
    url: string | undefined;
}

export type LocaleContextType = {
    locale: LocaleType;
    options: LocaleChoice[],
    swapLocales: () => void;
}

const defaultValue: Pick<LocaleContextType, 'locale'> = {
    locale: elGR
}

const options: LocaleChoice[] = [{
    key: 'lang.greek',
    value: elGR,
    url: 'https://upload.wikimedia.org/wikipedia/commons/thumb/5/5c/Flag_of_Greece.svg/1920px-Flag_of_Greece.svg.png'
}, {
    key: 'lang.english',
    value: enUS,
    url: 'https://upload.wikimedia.org/wikipedia/commons/thumb/8/83/Flag_of_the_United_Kingdom_%283-5%29.svg/1920px-Flag_of_the_United_Kingdom_%283-5%29.svg.png'
}]

const LocaleContext = React.createContext<LocaleContextType | null>(null);

export const LocaleContextProvider: React.FC<PropsWithChildren> = (props) => {
    const [locale, setLocale] = React.useState<LocaleType>(defaultValue.locale);

    i18n
        .use(initReactI18next) // passes i18n down to react-i18next
        .init({
            resources: {
                en: {
                    translation: englishTranslations
                },
                el: {
                    translation: greekTranslations
                }
            },
            lng: "el", // if you're using a language detector, do not define the lng option
            fallbackLng: "el",
            interpolation: {
                escapeValue: false
            }
        });

    useEffect(() => {
        if (!!locale && locale?.locale !== i18n.language) {
            i18n.changeLanguage(locale.locale)
            dayjs.locale(locale?.locale);
        }
    }, [locale]);

    const swapLocales = useCallback(() => {
        setLocale(options.find(opt => opt.value !== locale)?.value);
    }, [locale, options, setLocale]);

    return (
        <GoogleReCaptchaProvider reCaptchaKey={"6LcN2i0qAAAAAJST7lLm5aMMGZZpKUGxTwLYHDsj"} container={{element: 'hidden-div-element-re-captcha', parameters: {}}}>
            <LocaleContext.Provider value={{
                locale,
                options,
                swapLocales
            }}>
                <ConfigProvider locale={locale} theme={{
                    token: {
                        fontFamily: "Gill Sans, sans-serif"
                    }
                }}>
                    <App>

                        {props.children}
                    </App>
                </ConfigProvider>
            </LocaleContext.Provider>
        </GoogleReCaptchaProvider>
    )
}

export const useLocaleContext = () => {
    const context = useContext(LocaleContext);
    if (!context) {
        throw new Error("useLocaleContext must be used within LocaleContextProvider");
    }

    return context;
}

